import { WarehouseItemPhotoAction } from "@redotech/crdb-enums/fulfillment/warehouse-item-photo-action";
import { WarehouseItemWorkRequestKind } from "@redotech/crdb-enums/fulfillment/warehouse-item-work-request-kind";
import { WarehouseItemWorkRequestRejectionReason } from "@redotech/crdb-enums/fulfillment/warehouse-item-work-request-rejection-reason";
import { WarehouseItemWorkRequestStatus } from "@redotech/crdb-enums/fulfillment/warehouse-item-work-request-status";
import { inventoryItemIdFormat } from "@redotech/redo-inventory-id";
import { productIdFormat } from "@redotech/redo-product-id";
import {
  photoInstructionsIdFormat,
  photoInstructionStepIdFormat,
  warehouseItemPhotoCaptureIdFormat,
  warehouseItemWorkRequestIdFormat,
} from "@redotech/redo-warehouse-id";
import * as z from "zod";

export const ItemWorkRequestTableRowSchema = z.object({
  id: warehouseItemWorkRequestIdFormat.schema,
  inventoryItemId: inventoryItemIdFormat.schema,
  kind: z.enum(WarehouseItemWorkRequestKind),
  status: z.enum(WarehouseItemWorkRequestStatus),
  /** The merchant that asked for the work, not the 3PL doing it. */
  requestedByTeamName: z.string(),
  /** The 3PL team doing the work. */
  accountTeamName: z.string(),
  locationName: z.string(),
  /** The bin, when the item has been put away. */
  storageLocationName: z.string().nullable(),
  productId: productIdFormat.schema.nullable(),
  productTitle: z.string().nullable(),
  sku: z.string().nullable(),
  instructionsDescription: z.string().nullable(),
  photoCount: z.number(),
  completedAt: z.date().nullable(),
  /** Why the warehouse could not do the work; null unless status is rejected. */
  rejectionReason: z.enum(WarehouseItemWorkRequestRejectionReason).nullable(),
  rejectionNote: z.string().nullable(),
  createdAt: z.date(),
});

export type ItemWorkRequestTableRow = z.infer<
  typeof ItemWorkRequestTableRowSchema
>;

export const PhotoInstructionStepSchema = z.object({
  id: photoInstructionStepIdFormat.schema,
  position: z.number(),
  guidance: z.string(),
  examplePhotoUrl: z.string().nullable(),
});

/** A named set of directions the merchant maintains in warehouse settings. */
export const PhotoInstructionsSchema = z.object({
  id: photoInstructionsIdFormat.schema,
  description: z.string(),
  action: z.enum(WarehouseItemPhotoAction),
  steps: z.array(PhotoInstructionStepSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type PhotoInstructions = z.infer<typeof PhotoInstructionsSchema>;

export type PhotoInstructionStep = z.infer<typeof PhotoInstructionStepSchema>;

export const ItemPhotoCaptureSchema = z.object({
  id: warehouseItemPhotoCaptureIdFormat.schema,
  photoInstructionStepId: photoInstructionStepIdFormat.schema.nullable(),
  position: z.number(),
  url: z.string(),
});

export const ItemPhotoRequestDetailSchema = z.object({
  id: warehouseItemWorkRequestIdFormat.schema,
  inventoryItemId: inventoryItemIdFormat.schema,
  status: z.enum(WarehouseItemWorkRequestStatus),
  productTitle: z.string().nullable(),
  sku: z.string().nullable(),
  productImageUrl: z.string().nullable(),
  instructions: z.object({
    id: photoInstructionsIdFormat.schema,
    description: z.string(),
    action: z.enum(WarehouseItemPhotoAction),
    steps: z.array(PhotoInstructionStepSchema),
  }),
  photos: z.array(ItemPhotoCaptureSchema),
});

export type ItemPhotoRequestDetail = z.infer<
  typeof ItemPhotoRequestDetailSchema
>;

export type ItemPhotoCapture = z.infer<typeof ItemPhotoCaptureSchema>;
